/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ButtonGroup, Dialog, TextField, Typography2 } from '@coursera/cds-core';

import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';

import _t from 'i18n!nls/enterprise-legacy-learner-home';

type AddEmailStepProps = {
  emailField: string;
  onChangeEmail: (event: React.ChangeEvent<HTMLInputElement>) => void;
  getValidationErrors: () => string | false;
  onSubmitEmail: () => void;
  emailFieldError: string;
  loading: boolean;
  onClose: () => void;
  addRecoveryEmailButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  cancelButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  shouldDeferOnClose?: boolean;
};

const styles = {
  input: css`
    margin-top: var(--cds-spacing-400);
    width: 100%;
  `,
};

const { HeadingGroup, Content, Actions } = Dialog;

const AddEmailStep = ({
  emailField,
  onChangeEmail,
  getValidationErrors,
  onSubmitEmail,
  emailFieldError,
  loading,
  onClose,
  addRecoveryEmailButtonRef,
  cancelButtonRef,
  shouldDeferOnClose,
}: AddEmailStepProps) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  return (
    <>
      <HeadingGroup id="title">{_t('Protect your accomplishments')}</HeadingGroup>
      <Content>
        <Typography2 component="p" id="description">
          {thirdPartyOrganization?.name
            ? _t(
                'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your #{orgName} account.',
                { orgName: thirdPartyOrganization?.name }
              )
            : _t(
                'Add a personal recovery email to ensure access to your accomplishments in case you lose access to your organization account.'
              )}
        </Typography2>
        <TextField
          css={styles.input}
          value={emailField || ''}
          onChange={onChangeEmail}
          onBlur={getValidationErrors}
          label={_t('Recovery email address')}
          name="recovery-email"
          data-testid="recovery-email-input"
          validationStatus={emailFieldError ? 'error' : undefined}
          validationLabel={emailFieldError}
          placeholder={_t('Enter a personal email address (e.g., name@gmail.com)')}
        />
      </Content>
      <Actions>
        <ButtonGroup
          aria-label={_t('Submit or skip email')}
          primaryButtonProps={{
            children: _t('Add a recovery email'),
            onClick: onSubmitEmail,
            loading,
            ref: addRecoveryEmailButtonRef,
          }}
          alternateButtonProps={{
            children: shouldDeferOnClose ? _t('Remind me later') : _t('Cancel'),
            variant: shouldDeferOnClose ? 'ghost' : 'secondary',
            onClick: onClose,
            ref: cancelButtonRef,
          }}
        />
      </Actions>
    </>
  );
};

export default AddEmailStep;
